import { ApolloError, useApolloClient } from "@apollo/client";
import { formatDODID, isValidDODID } from "@ths/shared/utils";
import { Button, Card } from "@ths/ui";
import { Outline } from "@ths/ui/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { trackBasecampEvent } from "../analytics";
import {
  SelfCheckinLookupNotFoundError,
  presentSelfCheckinError,
  presentSelfCheckinLookupError,
} from "../api/selfCheckinError";
import {
  clearSelectedAssignment,
  clearStoredToken,
  getSelectedAssignmentId,
  getStoredToken,
  selectAssignment,
  storeToken,
  subscribeStoredToken,
} from "../api/token";
import CheckInOverlay from "../components/CheckInOverlay";
import CheckOutDialog from "../components/CheckOutDialog";
import { CheckoutReminderPreferences } from "../components/CheckoutReminderPreferences";
import GoodbyeScreen from "../components/GoodbyeScreen";
import GuestAccountMenu from "../components/GuestAccountMenu";
import type { CheckInContact } from "../components/ReminderConsentFields";
import Shell from "../components/Shell";
import Spinner from "../components/Spinner";
import StatusBadge from "../components/StatusBadge";
import StayNavigation from "../components/StayNavigation";
import { useCheckoutReminderOptionsQuery } from "../generated/graphql";
import {
  EndSelfCheckinSessionDocument,
  GetSelfCheckinReservationDocument,
  GetSelfCheckinSessionDocument,
  useGetSelfCheckinLookupLazyQuery,
  useGetSelfCheckOutChecklistLazyQuery,
  useSelfCheckOutMutation,
  useSelfCheckinMutation,
  type GetSelfCheckinReservationQuery,
  type GetSelfCheckinSessionQuery,
  type GetSelfCheckOutChecklistQuery,
  type SelfCheckinIdentityInput,
  SelfCheckinIdentityType,
  SelfCheckinLifecycleSource,
  type SelfCheckinLookupReservation,
} from "../generated/graphql";
import BasecampSelectStep from "../steps/BasecampSelectStep";
import CivilianLookupStep, {
  type CivilianLookupValues,
} from "../steps/CivilianLookupStep";
import ConfirmStep from "../steps/ConfirmStep";
import EdipiStep from "../steps/EdipiStep";
import GuestSettingsStep from "../steps/GuestSettingsStep";
import IdentityChoiceStep from "../steps/IdentityChoiceStep";
import InventoryStep from "../steps/InventoryStep";
import MenuStep from "../steps/MenuStep";
import ReservationStep from "../steps/ReservationStep";
import VehicleStep, { type ArrivalInput } from "../steps/VehicleStep";

import RequestFormPage from "./RequestFormPage";

type ReservationData = GetSelfCheckinReservationQuery["selfCheckinReservation"];
type ChecklistItem =
  GetSelfCheckOutChecklistQuery["selfCheckOutChecklist"][number];
type PendingContact = CheckInContact;

type PageStep =
  | "session-error"
  | "loading"
  | "menu"
  | "identity-choice"
  | "edipi"
  | "civilian-checkin"
  | "civilian-request"
  | "basecampSelect"
  | "confirm"
  | "vehicle"
  | "reservation"
  | "settings"
  | "inventory"
  | "request"
  | "goodbye";

interface BasecampPageProps {
  initialDestination?: "reservation" | "request" | "checkout" | "menu";
}

const buildDodIdentity = (edipi: string): SelfCheckinIdentityInput => ({
  type: SelfCheckinIdentityType.DodId,
  edipi: formatDODID(edipi),
});

const buildCivilianIdentity = (
  values: CivilianLookupValues,
): SelfCheckinIdentityInput => ({
  type: SelfCheckinIdentityType.CivilianNameDate,
  firstName: values.firstName,
  lastName: values.lastName,
  checkInDate: values.checkInDate,
  checkOutDate: values.checkOutDate,
});

const GuestPage = ({
  initialDestination = "reservation",
  onIdentityChange,
}: BasecampPageProps & { onIdentityChange: () => void }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<PageStep>("loading");
  useEffect(() => {
    // A long chooser can leave the next view scrolled past its stay controls.
    if (
      step === "basecampSelect" ||
      step === "reservation" ||
      step === "settings" ||
      step === "vehicle"
    ) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [step]);
  const [edipi, setEdipi] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkinIdentity, setCheckinIdentity] =
    useState<SelfCheckinIdentityInput | null>(null);
  const [civilianLookupStatus, setCivilianLookupStatus] = useState<
    "entry" | "not-found"
  >("entry");
  const [selectReservations, setSelectReservations] = useState<
    SelfCheckinLookupReservation[]
  >([]);
  const [selectedReservation, setSelectedReservation] =
    useState<SelfCheckinLookupReservation | null>(null);
  const reminderOptions = useCheckoutReminderOptionsQuery();
  const accountButtonRef = useRef<HTMLButtonElement>(null);
  const [pendingContact, setPendingContact] = useState<PendingContact | null>(
    null,
  );
  const [forceEmailEntry, setForceEmailEntry] = useState(false);
  const [showUpcomingOnly, setShowUpcomingOnly] = useState(false);
  const [openSelectedStay] = useState(
    initialDestination === "reservation" &&
      location.state?.basecampView === "reservation",
  );
  useEffect(() => {
    if (
      initialDestination === "reservation" &&
      (location.state?.basecampView === "upcoming" ||
        location.state?.basecampView === "reservation")
    ) {
      setShowUpcomingOnly(location.state.basecampView === "upcoming");
      // Consume this navigation intent so reopening the root still shows all stays.
      void navigate(location.pathname + location.search, {
        replace: true,
        state: null,
      });
    }
  }, [initialDestination, location, navigate]);
  const [reservationData, setReservationData] =
    useState<ReservationData | null>(null);
  const [isStale, setIsStale] = useState(false);
  const isRefreshing = false;
  const [checkingOut, setCheckingOut] = useState(false);
  const [goodbyeName, setGoodbyeName] = useState("");
  const [checkinOverlayName, setCheckinOverlayName] = useState<string | null>(
    null,
  );

  const [inventoryItems, setInventoryItems] = useState<ChecklistItem[]>([]);
  const [showCheckOutDialog, setShowCheckOutDialog] = useState(false);
  const [reservationError, setReservationError] = useState("");

  const apolloClient = useApolloClient();
  const operationRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      operationRef.current += 1;
    };
  }, []);
  const guardOperation = useCallback((token = getStoredToken()) => {
    const operation = operationRef.current;
    return () =>
      mountedRef.current &&
      operationRef.current === operation &&
      getStoredToken() === token;
  }, []);
  const [retryStay, setRetryStay] = useState<{
    assignmentId: string;
    destination: "reservation" | "request";
  } | null>(null);
  const resetStay = useCallback(() => {
    operationRef.current += 1;
    clearSelectedAssignment();
    setReservationData(null);
    setSelectedReservation(null);
    setPendingContact(null);
    setForceEmailEntry(false);
    setInventoryItems([]);
    setShowCheckOutDialog(false);
    setReservationError("");
    setError("");
    setLoading(false);
    setCheckingOut(false);
    setCheckinOverlayName(null);
  }, []);
  const [doLookup] = useGetSelfCheckinLookupLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [doCheckIn] = useSelfCheckinMutation({
    errorPolicy: "all",
    fetchPolicy: "no-cache",
  });
  const [doCheckOut] = useSelfCheckOutMutation();
  const [doFetchChecklist] = useGetSelfCheckOutChecklistLazyQuery({
    fetchPolicy: "no-cache",
  });

  const lookupFlow =
    initialDestination === "checkout"
      ? "checkout"
      : initialDestination === "request"
        ? "request"
        : "checkin";
  const isTokenBootstrapFlow =
    initialDestination === "request" || initialDestination === "checkout";
  const lookupStartStep =
    initialDestination === "checkout"
      ? "edipi"
      : initialDestination === "request"
        ? "request"
        : "identity-choice";

  const invalidateToken = useCallback(() => {
    operationRef.current += 1;
    clearStoredToken();
    onIdentityChange();
  }, [onIdentityChange]);

  const handleSignOut = useCallback(() => {
    const token = getStoredToken();
    invalidateToken();
    if (token) {
      // Local sign-out is immediate, including when the network is unavailable.
      void apolloClient
        .mutate({
          mutation: EndSelfCheckinSessionDocument,
          variables: { token },
          fetchPolicy: "no-cache",
        })
        .catch(() => {});
    }
  }, [apolloClient, invalidateToken]);

  const clearRejectedSession = useCallback(
    async (error: unknown, isCurrent: () => boolean) => {
      if (!isCurrent()) return true;
      if (hasErrorCode(error, "UNAUTHENTICATED")) {
        invalidateToken();
        return true;
      }
      if (!hasErrorCode(error, "NOT_FOUND")) return false;
      // Assignment failures use the same code as expired guest sessions. Ask the
      // session endpoint before discarding an identity that may own other stays.
      try {
        const result = await apolloClient.query<GetSelfCheckinSessionQuery>({
          query: GetSelfCheckinSessionDocument,
          variables: { token: getStoredToken() },
          fetchPolicy: "no-cache",
          errorPolicy: "all",
        });
        if (!isCurrent()) return true;
        if (
          result.errors?.some(
            (item) =>
              item.extensions?.code === "NOT_FOUND" ||
              item.extensions?.code === "UNAUTHENTICATED",
          )
        ) {
          invalidateToken();
          return true;
        }
      } catch {
        // An unavailable session endpoint does not prove the session has ended.
      }
      return !isCurrent();
    },
    [apolloClient, invalidateToken],
  );

  const loadReservationFromToken = useCallback(
    async (
      token: string,
      assignmentId: string,
      destination: "reservation" | "request" = initialDestination === "request"
        ? "request"
        : "reservation",
    ) => {
      const isCurrent = guardOperation(token);
      setRetryStay({ assignmentId, destination });
      try {
        const result = await apolloClient.query<GetSelfCheckinReservationQuery>(
          {
            query: GetSelfCheckinReservationDocument,
            variables: { token, assignmentId },
            fetchPolicy: "no-cache",
            errorPolicy: "all",
          },
        );
        if (!isCurrent()) return;
        if (result.errors?.length)
          throw new ApolloError({ graphQLErrors: result.errors });
        const reservation = result.data?.selfCheckinReservation;
        if (!reservation || reservation.checkedOutAt)
          throw new Error("Stay unavailable");
        setReservationData(reservation);
        setIsStale(false);
        setStep(destination);
      } catch (err) {
        if (!isCurrent()) return;
        if (await clearRejectedSession(err, isCurrent)) return;
        setError(
          "We couldn’t load this stay. Try again or choose another stay.",
        );
        setStep("session-error");
      }
    },
    [apolloClient, guardOperation, initialDestination, clearRejectedSession],
  );

  const loadSession = useCallback(
    async (selectedId: string | null = null) => {
      const token = getStoredToken();
      if (!token) {
        setStep(initialDestination === "menu" ? "menu" : lookupStartStep);
        return;
      }
      const isCurrent = guardOperation(token);
      setRetryStay(null);
      setStep("loading");
      try {
        const result = await apolloClient.query<GetSelfCheckinSessionQuery>({
          query: GetSelfCheckinSessionDocument,
          variables: { token },
          fetchPolicy: "no-cache",
          errorPolicy: "all",
        });
        if (!isCurrent()) return;
        if (result.errors?.length)
          throw new ApolloError({ graphQLErrors: result.errors });
        if (!result.data?.selfCheckinSession)
          throw new Error("Missing session");
        const reservations = result.data.selfCheckinSession.reservations;
        setSelectReservations(reservations);
        const selected = reservations.find(
          (stay) => stay.assignmentId === selectedId && stay.checkedInAt,
        );
        if (selected) {
          setSelectedReservation(selected);
          selectAssignment(selected.assignmentId);
          await loadReservationFromToken(token, selected.assignmentId);
        } else {
          clearSelectedAssignment();
          setStep("basecampSelect");
        }
      } catch (err) {
        if (!isCurrent()) return;
        // NOT_FOUND from the guest-session query is authoritative. The same code
        // from a stay query can mean only that assignment was removed.
        if (
          hasErrorCode(err, "NOT_FOUND") ||
          hasErrorCode(err, "UNAUTHENTICATED")
        ) {
          invalidateToken();
          return;
        }
        setError(
          "We couldn’t load your stays. Check your connection and try again.",
        );
        setStep("session-error");
      }
    },
    [
      apolloClient,
      guardOperation,
      initialDestination,
      invalidateToken,
      loadReservationFromToken,
      lookupStartStep,
    ],
  );

  useEffect(() => {
    const selectedId =
      isTokenBootstrapFlow || openSelectedStay
        ? getSelectedAssignmentId()
        : null;
    if (!selectedId) clearSelectedAssignment();
    void loadSession(selectedId);
  }, [isTokenBootstrapFlow, loadSession, openSelectedStay]);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      setShowUpcomingOnly(false);
      resetStay();
      void loadSession();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [loadSession, resetStay]);

  const lastTrackedStepRef = useRef<string | null>(null);
  useEffect(() => {
    if (step === "loading") return;
    const flow =
      step === "request"
        ? "request"
        : step === "inventory"
          ? "checkout"
          : step === "menu"
            ? "reservation"
            : lookupFlow;
    const trackedStepKey = `${flow}:${step}`;
    if (lastTrackedStepRef.current === trackedStepKey) return;
    lastTrackedStepRef.current = trackedStepKey;

    trackBasecampEvent("basecamp_flow_step_viewed", {
      flow,
      step,
      surface: "web",
    });
  }, [lookupFlow, step]);

  const handleLookup = useCallback(
    async (e?: FormEvent) => {
      e?.preventDefault();
      if (!isValidDODID(edipi) || loading) return;
      setError("");
      setLoading(true);
      const isCurrent = guardOperation();
      const identity = buildDodIdentity(edipi);
      setCheckinIdentity(identity);
      trackBasecampEvent("basecamp_lookup_submitted", {
        flow: lookupFlow,
        surface: "web",
      });
      try {
        const { data, error: gqlError } = await doLookup({
          variables: { identity },
        });
        if (!isCurrent()) return;
        if (gqlError) throw gqlError;
        if (!data?.selfCheckinLookup) throw new Error("Missing lookup data");
        const lookupReservations = data.selfCheckinLookup.reservations;
        const reservations =
          lookupFlow === "checkin"
            ? lookupReservations
            : lookupReservations.filter((reservation) =>
                Boolean(reservation.checkedInAt),
              );
        if (reservations.length === 0) {
          throw new SelfCheckinLookupNotFoundError(lookupFlow);
        }
        trackBasecampEvent("basecamp_lookup_succeeded", {
          basecampId:
            reservations.length === 1 ? reservations[0].basecampId : undefined,
          flow: lookupFlow,
          hasMultipleReservations: reservations.length > 1,
          reservationCount: reservations.length,
          surface: "web",
        });
        if (reservations.length === 1) {
          setSelectedReservation(reservations[0]);
          setStep("confirm");
        } else {
          setSelectReservations(reservations);
          setStep("basecampSelect");
        }
      } catch (err) {
        if (!isCurrent()) return;
        trackBasecampEvent("basecamp_lookup_failed", {
          errorCategory: "lookup_failed",
          flow: lookupFlow,
          surface: "web",
        });
        setError(presentSelfCheckinLookupError(err, lookupFlow).message);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    },
    [edipi, loading, doLookup, lookupFlow, guardOperation],
  );

  const handleCivilianLookup = useCallback(
    async (values: CivilianLookupValues) => {
      if (loading) return;
      const isCurrent = guardOperation();
      const identity = buildCivilianIdentity(values);
      setError("");
      setLoading(true);
      setCivilianLookupStatus("entry");
      setCheckinIdentity(identity);
      trackBasecampEvent("basecamp_lookup_submitted", {
        flow: lookupFlow,
        surface: "web",
      });
      try {
        const { data, error: gqlError } = await doLookup({
          variables: { identity },
        });
        if (!isCurrent()) return;
        if (gqlError) throw gqlError;
        if (!data?.selfCheckinLookup) throw new Error("Missing lookup data");
        const lookupReservations = data.selfCheckinLookup.reservations;
        const reservations =
          lookupFlow === "checkin"
            ? lookupReservations
            : lookupReservations.filter((reservation) =>
                Boolean(reservation.checkedInAt),
              );
        if (reservations.length !== 1) {
          trackBasecampEvent("basecamp_lookup_failed", {
            errorCategory: "lookup_failed",
            flow: lookupFlow,
            surface: "web",
          });
          setCivilianLookupStatus("not-found");
          return;
        }
        trackBasecampEvent("basecamp_lookup_succeeded", {
          basecampId: reservations[0].basecampId,
          flow: lookupFlow,
          hasMultipleReservations: false,
          reservationCount: 1,
          surface: "web",
        });
        setSelectedReservation(reservations[0]);
        setStep("confirm");
      } catch (err) {
        if (!isCurrent()) return;
        trackBasecampEvent("basecamp_lookup_failed", {
          errorCategory: "lookup_failed",
          flow: lookupFlow,
          surface: "web",
        });
        setError(presentSelfCheckinLookupError(err, lookupFlow).message);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    },
    [doLookup, loading, lookupFlow, guardOperation],
  );

  const handleBasecampSelect = useCallback(
    (reservation: SelfCheckinLookupReservation) => {
      // Loading and recovery screens retain the view that initiated the switch.
      const destination =
        step === "request"
          ? "request"
          : step === "loading" || step === "session-error"
            ? retryStay?.destination
            : undefined;
      resetStay();
      setShowUpcomingOnly(false);
      setSelectedReservation(reservation);
      const token = getStoredToken();
      if (!token) {
        setStep("confirm");
        return;
      }

      selectAssignment(reservation.assignmentId);
      if (initialDestination === "menu") {
        setStep("menu");
        return;
      }
      if (reservation.checkedInAt) {
        setStep("loading");
        void loadReservationFromToken(
          token,
          reservation.assignmentId,
          destination,
        );
        return;
      }
      setStep("vehicle");
    },
    [initialDestination, loadReservationFromToken, resetStay, retryStay, step],
  );

  const performCheckIn = useCallback(
    async (contact: PendingContact, vehicle: ArrivalInput) => {
      setError("");
      setLoading(true);
      let isCurrent = guardOperation();
      const hasVehicle = Boolean(
        selectedReservation?.hasVehicle ||
          vehicle.carPlateNumber ||
          vehicle.carMake,
      );
      try {
        const existingSessionToken = getStoredToken();
        const { data, errors } = await doCheckIn({
          variables: {
            input: {
              assignmentId: selectedReservation!.assignmentId,
              ...(existingSessionToken
                ? { sessionToken: existingSessionToken }
                : {
                    identity: checkinIdentity ?? buildDodIdentity(edipi),
                  }),
              source: SelfCheckinLifecycleSource.GuestDevice,
              ...(vehicle.phone ? { phone: vehicle.phone } : {}),
              ...(contact.email ? { email: contact.email } : {}),
              ...(contact.smsConsent
                ? {
                    phone: contact.phone,
                    smsConsent: true,
                    smsDisclosureVersion: contact.smsDisclosureVersion,
                  }
                : {}),
              ...(vehicle.carPlateNumber
                ? { carPlateNumber: vehicle.carPlateNumber }
                : {}),
              ...(vehicle.carMake ? { carMake: vehicle.carMake } : {}),
            },
          },
        });
        if (!isCurrent()) return;
        if (errors?.length || !data?.selfCheckin) {
          throw new ApolloError({ graphQLErrors: errors });
        }
        trackBasecampEvent("basecamp_checkin_completed", {
          basecampId: selectedReservation?.basecampId,
          emailProvided: Boolean(contact.email),
          flow: lookupFlow,
          hasVehicle,
          surface: "web",
        });
        storeToken(data.selfCheckin.token);
        isCurrent = guardOperation(data.selfCheckin.token);
        selectAssignment(selectedReservation!.assignmentId);
        if (isTokenBootstrapFlow) {
          await loadSession(selectedReservation!.assignmentId);
          if (isCurrent()) setLoading(false);
          return;
        }
        setCheckinOverlayName(
          `${data.selfCheckin.guestFirstName} ${data.selfCheckin.guestLastName}`,
        );
      } catch (err) {
        if (!isCurrent()) return;
        if (await clearRejectedSession(err, isCurrent)) return;
        const errorContext = isTokenBootstrapFlow
          ? initialDestination === "request"
            ? "request"
            : "checkout"
          : "checkin";
        const failure = presentSelfCheckinError(err, errorContext);
        setError(failure.message);
        // Keep signed-in guests on arrival details when correcting their email.
        // Identity lookup flows still collect email on confirmation.
        if (
          failure.action === "edit_email" ||
          failure.action === "edit_reminders"
        ) {
          if (
            getStoredToken() &&
            !isTokenBootstrapFlow &&
            !selectedReservation?.checkedInAt
          ) {
            setForceEmailEntry(failure.action === "edit_email");
            setStep("vehicle");
          } else if (failure.action === "edit_email") {
            setStep("confirm");
          }
        } else {
          trackBasecampEvent("basecamp_checkin_failed", {
            basecampId: selectedReservation?.basecampId,
            errorCategory: "checkin_failed",
            flow: lookupFlow,
            hasVehicle,
            surface: "web",
          });
        }
        setLoading(false);
      }
    },
    [
      guardOperation,
      clearRejectedSession,
      checkinIdentity,
      doCheckIn,
      edipi,
      initialDestination,
      isTokenBootstrapFlow,
      loadSession,
      lookupFlow,
      selectedReservation,
    ],
  );

  const handleConfirmSubmit = useCallback(
    (contact: PendingContact) => {
      setPendingContact(contact);
      const canReuseStoredStayDetails = Boolean(
        selectedReservation?.checkedInAt && selectedReservation.hasVehicle,
      );
      if (isTokenBootstrapFlow || canReuseStoredStayDetails) {
        // Request and checkout entries issue the existing stay token without
        // sending phone or vehicle fields that could overwrite stored data.
        // Repeat check-in does the same when this stay already has a vehicle.
        void performCheckIn(contact, {});
        return;
      }
      setStep("vehicle");
    },
    [isTokenBootstrapFlow, performCheckIn, selectedReservation],
  );

  const handleVehicleSubmit = useCallback(
    (vehicle: ArrivalInput) => {
      const contact: PendingContact = {
        ...pendingContact,
        ...vehicle,
        email: vehicle.email ?? pendingContact?.email ?? null,
      };
      setPendingContact(contact);
      void performCheckIn(contact, vehicle);
    },
    [pendingContact, performCheckIn],
  );

  const handleVehicleBack = useCallback(() => {
    setError("");
    if (getStoredToken()) {
      resetStay();
      void loadSession();
      return;
    }
    setStep("confirm");
  }, [loadSession, resetStay]);

  const handleOverlayComplete = useCallback(async () => {
    setCheckinOverlayName(null);
    setLoading(false);
    const token = getStoredToken();
    const assignmentId = getSelectedAssignmentId();
    if (!token || !assignmentId) {
      setStep(lookupStartStep);
      return;
    }
    await loadSession(assignmentId);
  }, [loadSession, lookupStartStep]);

  const performCheckOut = useCallback(
    async (missingItemIds: number[] | null) => {
      const token = getStoredToken();
      const assignmentId = getSelectedAssignmentId();
      if (!token || !assignmentId) return;
      const isCurrent = guardOperation(token);
      setCheckingOut(true);
      setReservationError("");
      try {
        const { errors } = await doCheckOut({
          variables: {
            token,
            assignmentId,
            missingItemIds: missingItemIds ?? null,
            source: SelfCheckinLifecycleSource.GuestDevice,
          },
        });
        if (!isCurrent()) return;
        if (errors?.length) throw new ApolloError({ graphQLErrors: errors });
        clearSelectedAssignment();
        setSelectReservations((stays) =>
          stays.filter((stay) => stay.assignmentId !== assignmentId),
        );
        trackBasecampEvent("basecamp_checkout_completed", {
          flow: "checkout",
          missingItemCount: missingItemIds?.length ?? 0,
          surface: "web",
        });
        setGoodbyeName(
          reservationData
            ? `${reservationData.guestFirstName} ${reservationData.guestLastName}`
            : "",
        );
        setShowCheckOutDialog(false);
        setStep("goodbye");
      } catch (err) {
        if (!isCurrent()) return;
        if (await clearRejectedSession(err, isCurrent)) return;
        setReservationError(presentSelfCheckinError(err, "checkout").message);
        trackBasecampEvent("basecamp_checkout_failed", {
          errorCategory: "checkout_failed",
          flow: "checkout",
          missingItemCount: missingItemIds?.length ?? 0,
          surface: "web",
        });
      } finally {
        if (isCurrent()) setCheckingOut(false);
      }
    },
    [reservationData, doCheckOut, guardOperation, clearRejectedSession],
  );

  const handleCheckOut = useCallback(async () => {
    const token = getStoredToken();
    const assignmentId = getSelectedAssignmentId();
    if (!token || !assignmentId) return;
    const isCurrent = guardOperation(token);
    setReservationError("");
    setCheckingOut(true);
    try {
      const {
        data,
        error: fetchError,
        errors: fetchErrors,
      } = await doFetchChecklist({
        variables: { token, assignmentId },
      });
      if (!isCurrent()) return;
      if (fetchError || fetchErrors?.length) {
        throw (
          fetchError ?? new ApolloError({ graphQLErrors: fetchErrors ?? [] })
        );
      }
      const items = data?.selfCheckOutChecklist ?? [];
      trackBasecampEvent("basecamp_checkout_started", {
        flow: "checkout",
        hasChecklist: items.length > 0,
        surface: "web",
      });
      if (items.length === 0) {
        setCheckingOut(false);
        setShowCheckOutDialog(true);
        return;
      }
      setInventoryItems(items);
      setStep("inventory");
    } catch (err) {
      if (!isCurrent()) return;
      if (await clearRejectedSession(err, isCurrent)) return;
      setReservationError(presentSelfCheckinError(err, "checkout").message);
    } finally {
      if (isCurrent()) setCheckingOut(false);
    }
  }, [doFetchChecklist, guardOperation, clearRejectedSession]);

  // Auto-fire the checkout flow once when arriving at the reservation step
  // with intent="checkout" (e.g. via the /checkout entry route). The ref
  // resets before loading another selected stay.
  const autoCheckoutTriggeredRef = useRef(false);
  useEffect(() => {
    if (step === "edipi" || step === "loading" || step === "basecampSelect")
      autoCheckoutTriggeredRef.current = false;
  }, [step]);
  useEffect(() => {
    if (
      !autoCheckoutTriggeredRef.current &&
      initialDestination === "checkout" &&
      step === "reservation" &&
      reservationData
    ) {
      autoCheckoutTriggeredRef.current = true;
      void handleCheckOut();
    }
  }, [step, reservationData, initialDestination, handleCheckOut]);

  const handleInventorySubmit = useCallback(
    (missingItemIds: number[]) => {
      trackBasecampEvent("basecamp_inventory_submitted", {
        flow: "checkout",
        missingItemCount: missingItemIds.length,
        surface: "web",
      });
      void performCheckOut(missingItemIds.length > 0 ? missingItemIds : null);
    },
    [performCheckOut],
  );

  const handleInventoryCancel = useCallback(() => {
    setInventoryItems([]);
    setReservationError("");
    setStep("reservation");
  }, []);

  const handleDialogConfirm = useCallback(() => {
    void performCheckOut(null);
  }, [performCheckOut]);

  const handleDialogCancel = useCallback(() => {
    setShowCheckOutDialog(false);
  }, []);

  const handleBackToStart = useCallback(() => {
    setShowUpcomingOnly(false);
    resetStay();
    if (getStoredToken()) {
      void loadSession();
      return;
    }
    setStep(lookupStartStep);
    setEdipi("");
    setError("");
    setCheckinIdentity(null);
    setCivilianLookupStatus("entry");
    setSelectReservations([]);
    setSelectedReservation(null);
    setPendingContact(null);
  }, [lookupStartStep, loadSession, resetStay]);

  const handleUpcomingStays = useCallback(() => {
    if (initialDestination !== "reservation") {
      void navigate("/", { state: { basecampView: "upcoming" } });
      return;
    }
    resetStay();
    setShowUpcomingOnly(true);
    void loadSession();
  }, [initialDestination, loadSession, navigate, resetStay]);

  const handleCloseRequest = useCallback(() => {
    // Stop a pending stay response from reopening the report after Close.
    operationRef.current += 1;
    if (initialDestination === "request") {
      if (!getStoredToken()) {
        void navigate("/menu", { replace: true });
        return;
      }
      void navigate("/", {
        replace: true,
        state: { basecampView: "reservation" },
      });
      return;
    }
    if (reservationData) {
      setStep("reservation");
      return;
    }
    const assignmentId = getSelectedAssignmentId();
    resetStay();
    void loadSession(assignmentId);
  }, [initialDestination, loadSession, navigate, reservationData, resetStay]);

  const upcomingCount = selectReservations.filter(
    (stay) => !stay.checkedInAt,
  ).length;
  const guest = selectedReservation ?? selectReservations[0] ?? reservationData;
  const guestName = [guest?.guestFirstName, guest?.guestLastName]
    .filter(Boolean)
    .join(" ");
  const isRequestFlow =
    initialDestination === "request" ||
    step === "request" ||
    ((step === "loading" || step === "session-error") &&
      retryStay?.destination === "request");
  const reminderAssignmentId = getSelectedAssignmentId();
  const canManageReminders = Boolean(
    reminderAssignmentId &&
      reservationData?.checkedInAt &&
      !reservationData.checkedOutAt &&
      !isStale &&
      !isRefreshing &&
      reminderOptions.data?.checkoutReminderOptions.enabled &&
      (reminderOptions.data.checkoutReminderOptions.smsAvailable ||
        reminderOptions.data.checkoutReminderOptions.emailAvailable),
  );
  const headerAction = isRequestFlow ? (
    <Button
      type="button"
      variant="ghost"
      aria-label="Close issue report"
      onClick={handleCloseRequest}
      className="size-11 shrink-0 p-0 text-on-navy/90 hover:bg-on-navy/10 focus-visible:ring-on-navy focus-visible:ring-offset-navy-strong"
    >
      <Outline.XMarkIcon className="size-5" aria-hidden="true" />
    </Button>
  ) : (
    <GuestAccountMenu
      guestName={guestName}
      onSignOut={handleSignOut}
      triggerRef={accountButtonRef}
      onSettings={
        canManageReminders && (step === "reservation" || step === "settings")
          ? () => setStep("settings")
          : undefined
      }
    />
  );

  const chooserReservations = showUpcomingOnly
    ? selectReservations.filter((stay) => !stay.checkedInAt)
    : isTokenBootstrapFlow || initialDestination === "menu"
      ? selectReservations.filter((stay) => Boolean(stay.checkedInAt))
      : selectReservations;

  const navigation = getStoredToken() ? (
    (step === "basecampSelect" && !showUpcomingOnly) ||
    selectReservations.length === 0 ? (
      <div className="relative z-10 px-6 pt-4">
        <div className="mx-auto flex w-full max-w-sm items-center justify-end gap-2 pb-4">
          {initialDestination !== "reservation" && upcomingCount > 0 && (
            <Button
              variant="ghost"
              onClick={handleUpcomingStays}
              className="text-on-navy/90 hover:bg-on-navy/10 focus-visible:ring-on-navy focus-visible:ring-offset-navy-strong"
            >
              Upcoming ({upcomingCount})
            </Button>
          )}
          {headerAction}
        </div>
      </div>
    ) : (
      <StayNavigation
        reservations={selectReservations}
        selectedAssignmentId={getSelectedAssignmentId()}
        onSelect={handleBasecampSelect}
        onUpcomingStays={handleUpcomingStays}
        headerAction={headerAction}
        welcomeMessage={
          step === "reservation" && reservationData
            ? `Welcome, ${reservationData.guestFirstName}`
            : undefined
        }
      />
    )
  ) : null;

  if (step === "session-error")
    return (
      <Shell navigation={navigation}>
        <main className="relative z-10 flex flex-1 justify-center px-6 pt-6">
          <Card
            surface="bordered"
            className="h-fit w-full max-w-sm space-y-4 border-on-navy/20 bg-navy/60 p-5 text-on-navy"
          >
            <h1 className="text-title text-on-navy">Unable to load</h1>
            <p role="alert" className="text-on-navy/75">
              {error}
            </p>
            <Button
              className="w-full border border-on-navy/30 focus-visible:ring-on-navy focus-visible:ring-offset-navy-strong"
              onClick={() => {
                const token = getStoredToken();
                setStep("loading");
                if (token && retryStay)
                  void loadReservationFromToken(
                    token,
                    retryStay.assignmentId,
                    retryStay.destination,
                  );
                else void loadSession();
              }}
            >
              Try again
            </Button>
          </Card>
        </main>
      </Shell>
    );

  if (step === "goodbye")
    return (
      <GoodbyeScreen guestName={goodbyeName} onComplete={handleBackToStart} />
    );

  if (step === "loading") {
    return (
      <Shell showGrid={false} navigation={navigation}>
        <div className="flex flex-1 items-center justify-center">
          <Spinner className="size-6 text-white/30" />
        </div>
      </Shell>
    );
  }

  if (step === "menu") {
    return (
      <Shell navigation={navigation}>
        <MenuStep
          onReportIssue={() => navigate("/request")}
          onCheckOut={() => navigate("/checkout")}
        />
      </Shell>
    );
  }

  if (step === "reservation" && reservationData) {
    return (
      <Shell navigation={navigation}>
        <ReservationStep
          reservation={reservationData}
          onCheckOut={handleCheckOut}
          isCheckingOut={checkingOut}
          isStale={isStale}
          isRefreshing={isRefreshing}
          onReportIssue={() => setStep("request")}
          error={reservationError}
        />
        {canManageReminders &&
          reminderAssignmentId &&
          !checkinOverlayName &&
          !showCheckOutDialog &&
          !checkingOut &&
          initialDestination === "reservation" && (
            <CheckoutReminderPreferences
              key={reminderAssignmentId}
              token={reservationData.token}
              assignmentId={reminderAssignmentId}
              mode="prompt"
              returnFocusRef={accountButtonRef}
            />
          )}
        <CheckOutDialog
          open={showCheckOutDialog}
          loading={checkingOut}
          onConfirm={handleDialogConfirm}
          onCancel={handleDialogCancel}
        />
        {checkinOverlayName && (
          <CheckInOverlay
            name={checkinOverlayName}
            onComplete={handleOverlayComplete}
          />
        )}
      </Shell>
    );
  }

  if (step === "settings" && reservationData && reminderAssignmentId) {
    return (
      <Shell navigation={navigation}>
        <GuestSettingsStep
          key={reminderAssignmentId}
          token={reservationData.token}
          assignmentId={reminderAssignmentId}
          onBack={() => setStep("reservation")}
        />
      </Shell>
    );
  }

  if (step === "inventory" && reservationData) {
    return (
      <Shell navigation={navigation}>
        <InventoryStep
          roomNumber={reservationData.roomNumber}
          items={inventoryItems}
          isSubmitting={checkingOut}
          error={reservationError}
          onCancel={handleInventoryCancel}
          onSubmit={handleInventorySubmit}
        />
      </Shell>
    );
  }

  if (step === "request") {
    return (
      <Shell plain navigation={navigation}>
        <RequestFormPage
          key={getSelectedAssignmentId()}
          sessionToken={reservationData?.token}
          assignmentId={
            reservationData
              ? (getSelectedAssignmentId() ?? undefined)
              : undefined
          }
          basecamp={
            selectedReservation
              ? {
                  id: selectedReservation.basecampId,
                  name: selectedReservation.basecampName,
                }
              : undefined
          }
          room={
            reservationData
              ? {
                  basecampRoomId: String(reservationData.roomId),
                  buildingName: reservationData.buildingName ?? "",
                  roomNumber: reservationData.roomNumber,
                }
              : undefined
          }
          contact={{
            phone: selectedReservation?.guestPhone ?? "",
            email: reservationData?.guestEmail ?? "",
          }}
          onClose={handleCloseRequest}
        />
      </Shell>
    );
  }

  if (step === "identity-choice") {
    return (
      <Shell navigation={navigation}>
        <IdentityChoiceStep
          flow={initialDestination === "request" ? "request" : "reservation"}
          onSoldier={() => {
            setError("");
            setCivilianLookupStatus("entry");
            setStep("edipi");
          }}
          onCivilian={() => {
            setError("");
            setEdipi("");
            setCheckinIdentity(null);
            setCivilianLookupStatus("entry");
            setStep(
              initialDestination === "request"
                ? "civilian-request"
                : "civilian-checkin",
            );
          }}
        />
      </Shell>
    );
  }

  if (step === "civilian-checkin" || step === "civilian-request") {
    return (
      <Shell navigation={navigation}>
        <CivilianLookupStep
          onSubmit={handleCivilianLookup}
          isSubmitting={loading}
          error={error}
          status={civilianLookupStatus}
          flow={step === "civilian-request" ? "request" : "reservation"}
          onRetry={() => {
            setError("");
            setCivilianLookupStatus("entry");
          }}
          onBack={() => {
            setError("");
            setCheckinIdentity(null);
            setCivilianLookupStatus("entry");
            setStep("identity-choice");
          }}
        />
      </Shell>
    );
  }

  return (
    <Shell navigation={navigation}>
      {step === "edipi" ? (
        <EdipiStep
          edipi={edipi}
          setEdipi={setEdipi}
          onSubmit={handleLookup}
          isSubmitting={loading}
          error={error}
          mode={
            initialDestination === "request"
              ? "request"
              : initialDestination === "checkout"
                ? "checkout"
                : "reservation"
          }
          onBack={
            initialDestination === "reservation"
              ? () => {
                  setError("");
                  setEdipi("");
                  setCheckinIdentity(null);
                  setCivilianLookupStatus("entry");
                  setStep("identity-choice");
                }
              : undefined
          }
        />
      ) : (
        <>
          {step !== "basecampSelect" && step !== "vehicle" && (
            <header className="relative z-10 px-6 pb-4 pt-10 text-center sm:pt-14">
              <StatusBadge
                label={
                  initialDestination === "request"
                    ? "Issue Report"
                    : initialDestination === "checkout"
                      ? "Guest Check-Out"
                      : initialDestination === "menu"
                        ? "Guest Services"
                        : "Guest Check-In"
                }
              />
            </header>
          )}
          <main
            className={`relative z-10 flex flex-1 flex-col items-center px-6 pb-12 ${step === "basecampSelect" || step === "vehicle" ? "pt-6 sm:pt-8" : "justify-center"}`}
          >
            {step === "basecampSelect" && chooserReservations.length === 0 && (
              <Card
                surface="bordered"
                className="w-full max-w-sm space-y-4 border-on-navy/20 bg-navy/60 p-5 text-on-navy"
              >
                <h1 className="text-title text-on-navy">
                  {showUpcomingOnly ? "No upcoming stays" : "No stays to show"}
                </h1>
                <p className="text-on-navy/75">
                  {showUpcomingOnly
                    ? "Your upcoming reservations will appear here when available."
                    : initialDestination === "reservation"
                      ? "You’re still signed in. Your current and upcoming stays will appear here when available."
                      : "You’re still signed in. There are no active stays for guest services."}
                </p>
                <Button
                  onClick={() => void loadSession()}
                  className="w-full border border-on-navy/30 focus-visible:ring-on-navy focus-visible:ring-offset-navy-strong"
                >
                  Refresh stays
                </Button>
              </Card>
            )}
            {step === "basecampSelect" && chooserReservations.length > 0 && (
              <BasecampSelectStep
                reservations={chooserReservations}
                upcomingOnly={showUpcomingOnly}
                flow={
                  getStoredToken() &&
                  (initialDestination === "reservation" ||
                    initialDestination === "menu")
                    ? initialDestination === "menu"
                      ? "menu"
                      : "session"
                    : lookupFlow
                }
                onSelect={handleBasecampSelect}
                onCancel={getStoredToken() ? undefined : handleBackToStart}
              />
            )}
            {step === "confirm" && selectedReservation && (
              <ConfirmStep
                key={selectedReservation.assignmentId}
                lookup={selectedReservation}
                isSubmitting={loading}
                error={error}
                initialEmail={pendingContact?.email ?? ""}
                flow={lookupFlow === "checkin" ? "reservation" : lookupFlow}
                onConfirm={handleConfirmSubmit}
                onBack={handleBackToStart}
              />
            )}
            {step === "vehicle" && !isTokenBootstrapFlow && (
              <VehicleStep
                key={selectedReservation?.assignmentId}
                stay={selectedReservation ?? undefined}
                showStaySummary={Boolean(getStoredToken())}
                isSubmitting={loading}
                error={error}
                requiresEmail={
                  Boolean(getStoredToken()) &&
                  (forceEmailEntry || !selectedReservation?.hasEmail)
                }
                initialEmail={pendingContact?.email ?? ""}
                initialPhone={
                  pendingContact?.phone ?? selectedReservation?.guestPhone ?? ""
                }
                onSubmit={handleVehicleSubmit}
                onBack={handleVehicleBack}
              />
            )}
          </main>
        </>
      )}

      {checkinOverlayName && (
        <CheckInOverlay
          name={checkinOverlayName}
          onComplete={handleOverlayComplete}
        />
      )}
    </Shell>
  );
};

function hasErrorCode(error: unknown, code: string): boolean {
  return (
    error instanceof ApolloError &&
    error.graphQLErrors.some((item) => item.extensions?.code === code)
  );
}

export default function BasecampPage(props: BasecampPageProps) {
  const [identityVersion, setIdentityVersion] = useState(0);
  const resetIdentity = useCallback(
    () => setIdentityVersion((value) => value + 1),
    [],
  );
  useEffect(() => subscribeStoredToken(resetIdentity), [resetIdentity]);
  return (
    <GuestPage
      key={identityVersion}
      {...props}
      onIdentityChange={resetIdentity}
    />
  );
}
